<template>
  <div class="page-content one-x-exemption">
    <div class="page-header">
      <div class="page-header-line"></div>
      <h5>1+X申免</h5>
    </div>

    <div class="page-body">
      <!-- 基本信息 -->
      <div class="section">
        <div class="section-title">基本信息</div>
        <div class="exam-info-row">
          <div class="exam-info-item">
            <span class="field-label">姓名：</span>
            <span class="field-value">{{ user.name || '—' }}</span>
          </div>
          <div class="exam-info-item">
            <span class="field-label">准考证号：</span>
            <span class="field-value">{{ user.certiNo || '—' }}</span>
          </div>
          <div class="exam-info-item">
            <span class="field-label">专业：</span>
            <span class="field-value">{{ formatCodeName(user.specialtyCode, user.specialtyName) }}</span>
          </div>
          <div class="exam-info-item">
            <span class="field-label">层次：</span>
            <span class="field-value">{{ formatEduLevel(user.eduLevel) }}</span>
          </div>
          <div class="exam-info-item">
            <span class="field-label">助学点：</span>
            <span class="field-value">{{ formatCodeName(user.helpCode, user.helpName) }}</span>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- 考次信息 -->
      <div class="section">
        <div class="section-title">考次信息</div>
        <div class="exam-info-row">
          <div class="exam-info-item">
            <span class="field-label">考次：</span>
            <el-select v-model="selectedExam" placeholder="请选择考次" class="exam-select">
              <el-option
                v-for="item in examOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </div>
          <div class="exam-info-item">
            <span class="field-label">资料提交时间：</span>
            <span class="field-value">{{ submitTimeText }}</span>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- 证书信息 -->
      <div class="section">
        <div v-if="hasApplication" class="section-title">证书信息</div>

        <!-- 该考次无此学生记录 -->
        <div v-if="!hasApplication" class="no-batch-tip">暂未报名</div>

        <template v-else>
        <!-- 未提交状态：表单 -->
        <div v-if="application.status === 0" class="cert-form">
          <div class="form-item">
            <span class="field-label">证书名称：</span>
            <span class="field-value">{{ application.certName }}</span>
          </div>
          <div class="form-item">
            <span class="field-label">证书编号：<span class="required-mark">*</span></span>
            <el-input
              v-model="application.certNo"
              placeholder="请输入证书编号"
              class="form-input"
              clearable
            ></el-input>
          </div>
          <div class="form-item">
            <span class="field-label">证书图片：<span class="required-mark">*</span></span>
            <div class="form-item-inner">
              <div class="cert-upload">
                <input
                  ref="certFileInput"
                  type="file"
                  accept="image/jpeg,image/png,image/bmp"
                  class="cert-file-input"
                  @change="handleFileChange"
                />
                <el-button type="primary" size="small" @click="triggerFileUpload">上传证书图片</el-button>
                <span class="upload-tip">仅支持上传图片格式（JPG/PNG/BMP）</span>
              </div>
              <div v-if="application.certPhotoName" class="cert-file-name">
                已选择：{{ application.certPhotoName }}
              </div>
            </div>
          </div>

          <div class="submit-row">
            <el-button type="primary" :loading="submitting" @click="submitApplication">提交证书信息</el-button>
          </div>
        </div>

        <!-- 已提交/已审核状态：只读展示，打回修改状态可编辑 -->
        <div v-if="application.status !== 0" class="cert-display">
          <div class="cert-display-row">
            <div class="cert-display-col">
              <div class="certificate-field">
                <span class="field-label">证书名称：</span>
                <span class="field-value">{{ application.certName }}</span>
              </div>
              <div class="certificate-field">
                <span class="field-label">证书图片：<span class="required-mark">*</span></span>
                <!-- 打回修改：可重新上传 -->
                <template v-if="application.status === 4">
                  <div class="form-item-inner">
                    <div class="cert-upload">
                      <input
                        ref="certFileInput"
                        type="file"
                        accept="image/jpeg,image/png,image/bmp"
                        class="cert-file-input"
                        @change="handleFileChange"
                      />
                      <el-button type="primary" size="small" @click="triggerFileUpload">重新上传</el-button>
                    </div>
                    <div v-if="application.certPhotoName" class="cert-file-name">
                      已选择：{{ application.certPhotoName }}
                    </div>
                  </div>
                </template>
                <!-- 非打回修改：只读 -->
                <template v-else>
                  <a
                    class="certificate-file-link"
                    @click.prevent="previewCertPhoto"
                  >{{ application.certPhotoName || '—' }}</a>
                  <XwImg
                    ref="certPhotoImg"
                    :src="certPhotoUrl"
                    fit="contain"
                    style="display: none"
                  />
                </template>
              </div>
            </div>
            <div class="cert-display-col">
              <div class="certificate-field">
                <span class="field-label">证书编号：<span class="required-mark">*</span></span>
                <!-- 打回修改：可编辑 -->
                <el-input
                  v-if="application.status === 4"
                  v-model="application.certNo"
                  placeholder="请输入证书编号"
                  class="form-input"
                  clearable
                />
                <span v-else class="field-value">{{ application.certNo }}</span>
              </div>
              <div class="certificate-field">
                <span class="field-label">审核状态：</span>
                <span :class="application.status === 4 ? 'status-reject' : 'status-pending'">{{ statusMap[application.status] }}</span>
              </div>
            </div>
          </div>
          <!-- 审核意见 -->
          <div v-if="application.auditRemark" class="audit-feedback">
            <div class="certificate-field">
              <span class="field-label">审核意见：</span>
              <span class="field-value">{{ application.auditRemark }}</span>
            </div>
            <div v-if="application.auditTime" class="certificate-field audit-feedback-time">
              <span class="field-label">审核时间：</span>
              <span class="field-value">{{ application.auditTime }}</span>
            </div>
          </div>
          <!-- 打回修改：提交按钮 -->
          <div v-if="application.status === 4" class="submit-row">
            <el-button type="primary" :loading="submitting" @click="submitApplication">提交证书信息</el-button>
          </div>
        </div>
        </template>
      </div>

      <!-- 日志（有审核日志时显示） -->
      <div v-if="application.status !== 0 && auditLogsReversed.length" class="section">
        <div class="divider"></div>
        <div class="section-title">日志</div>
        <div class="audit-timeline">
          <div
            v-for="(item, index) in auditLogsReversed"
            :key="index"
            class="audit-timeline-item"
          >
            <span class="timeline-dot"></span>
            <span v-if="index < auditLogsReversed.length - 1" class="audit-timeline-line"></span>
            <div class="audit-log-card">
              <div class="audit-log-header">
                <div class="audit-field">
                  <span class="audit-label">审核时间：</span>
                  <span class="audit-value">{{ item.createTime }}</span>
                </div>
                <div class="audit-field">
                  <span class="audit-label">操作人：</span>
                  <span class="audit-value">{{ item.operName || '—' }}</span>
                </div>
               <!-- <div class="audit-field">
                 <span class="audit-label">状态：</span>
                  <span
                    class="audit-status"
                    :class="getStatusClass(item.afterStatus)"
                  >
                    {{ statusMap[item.afterStatus] }}
                  </span>
                </div>-->
              </div>
              <div class="audit-log-remark">
                <span class="audit-label">备注：</span>
                <span class="audit-value">{{ item.operRemark || '—' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { edulevel } from '@/utils/enums'
import { getCertExemptCurrent, getExamBatchSelect, submitCertExempt, resubmitCertExempt } from '@/api/certExempt'

export default {
  name: 'OneXExemption',
  components: {
    XwImg: () => import('@/components/XwImg.vue')
  },
  data() {
    return {
      examList: [],
      selectedExam: '',
      statusMap: {
        0: '待补材料',
        1: '待审核',
        2: '通过',
        3: '不通过',
        4: '打回修改'
      },
      application: {
        id: '',
        certName: '',
        certNo: '',
        certPhotoName: '',
        certPhotoUrl: '',
        certPhotoFile: null,
        certPhotoObjectUrl: '',
        status: 0,
        batchId: '',
        auditRemark: '',
        auditTime: '',
        submitStartTime: '',
        submitEndTime: '',
        examCode: ''
      },
      auditLogs: [],
      submitting: false,
      isInitLoading: false,
      hasApplication: false
    }
  },
  computed: {
    ...mapState(['user']),
    examOptions() {
      return this.examList.map(item => ({
        label: item.batchName,
        value: item.batchId
      }))
    },
    submitTimeText() {
      const app = this.application
      if (app.submitStartTime && app.submitEndTime) {
        return `${app.submitStartTime} 至 ${app.submitEndTime}`
      }
      const exam = this.examList.find(item => item.batchId === this.selectedExam)
      if (exam && exam.submitStartTime && exam.submitEndTime) {
        return `${exam.submitStartTime} 至 ${exam.submitEndTime}`
      }
      return '—'
    },
    certPhotoUrl() {
      return this.application.certPhotoUrl || this.application.certPhotoObjectUrl || ''
    },
    auditLogsReversed() {
      return [...this.auditLogs].sort((a, b) => {
        return new Date(b.createTime) - new Date(a.createTime)
      })
    }
  },
  watch: {
    selectedExam(newVal, oldVal) {
      if (this.isInitLoading || newVal === oldVal) return
      this.loadApplication(newVal)
    }
  },
  beforeDestroy() {
    if (this.application.certPhotoObjectUrl) {
      URL.revokeObjectURL(this.application.certPhotoObjectUrl)
    }
  },
  created() {
    this.loadInitData()
  },
  methods: {
    async loadInitData() {
      this.isInitLoading = true
      try {
        // 先获取考次列表，默认选中第一个考次
        const examList = await getExamBatchSelect()
        this.examList = examList || []
        if (this.examList.length) {
          if (!this.selectedExam) {
            this.selectedExam = this.defaultExamId()
          }
          // 用选中考次的 batchId 请求初始化信息
          await this.loadApplication(this.selectedExam)
        } else {
          // 无可申请考次时不请求初始化接口，避免空 examBatchId 导致后端报错
          this.selectedExam = ''
          this.hasApplication = false
          this.resetApplication()
        }
      } catch (e) {
        // 错误提示已由 request 封装统一处理
      } finally {
        this.isInitLoading = false
      }
    },
    async loadApplication(examBatchId) {
      // 无考次ID时直接按"暂无申请"处理，不发起请求
      if (!examBatchId) {
        this.hasApplication = false
        this.resetApplication()
        return
      }
      try {
        const initData = await getCertExemptCurrent(examBatchId || '')
        // 兼容两种返回结构：
        // 1. 扁平结构：data 直接为申请对象（含 id），如传空 examBatchId 时
        // 2. 包装结构：data.application 为申请对象（原接口文档结构），无申请时为 null
        const wrapped = initData && initData.application
        const app = initData && initData.id
          ? initData
          : (wrapped && wrapped.id ? wrapped : null)
        if (app) {
          this.hasApplication = true
          this.application.id = app.id || ''
          this.application.certName = app.certName || ''
          this.application.certNo = app.certNo || ''
          this.application.certPhotoUrl = app.certPhotoUrl || ''
          this.application.certPhotoName = app.certPhotoName || app.fileName || this.getFileName(app.certPhotoUrl)
          this.application.status = app.status != null ? Number(app.status) : 0
          this.application.batchId = app.batchId || ''
          this.application.auditRemark = app.auditRemark || ''
          this.application.auditTime = app.auditTime || ''
          this.application.submitStartTime = app.submitStartTime || ''
          this.application.submitEndTime = app.submitEndTime || ''
          this.application.examCode = app.examCode || ''
          this.auditLogs = (app.auditLogs || []).map(log => ({
            ...log,
            createTime: log.operationTime || log.createTime || log.auditTime || '',
            operName: log.operatorName || log.operName || '',
            operRemark: log.auditRemark || log.operRemark || log.operationRemark || log.remark || log.content || ''
          }))
        } else {
          this.hasApplication = false
          this.resetApplication()
        }
      } catch (e) {
        // 错误提示已由 request 封装统一处理
      }
    },
    resetApplication() {
      this.application.id = ''
      this.application.certName = ''
      this.application.certNo = ''
      this.application.certPhotoName = ''
      this.application.certPhotoUrl = ''
      this.application.status = 0
      this.application.batchId = ''
      this.application.auditRemark = ''
      this.application.auditTime = ''
      this.application.submitStartTime = ''
      this.application.submitEndTime = ''
      this.application.examCode = ''
      this.auditLogs = []
    },
    defaultExamId() {
      return (this.examList[0] && this.examList[0].batchId) || ''
    },
    getFileName(url) {
      if (!url) return ''
      try {
        return decodeURIComponent(url.split('?')[0].split('/').pop() || '')
      } catch (e) {
        return url.split('?')[0].split('/').pop() || ''
      }
    },
    resolveUploadFileUrl(uploadRes) {
      if (!uploadRes) return ''
      if (typeof uploadRes === 'string') return uploadRes
      if (typeof uploadRes === 'object') {
        return uploadRes.res || uploadRes.fullpath || uploadRes.url || uploadRes.data || ''
      }
      return ''
    },
    async uploadCertPhoto(file) {
      const formData = new FormData()
      formData.append('file', file)
      const uploadRes = await this.$http.post('/student/pc/common/uploadFile2', formData, {
        'Content-Type': 'multipart/form-data'
      })
      return this.resolveUploadFileUrl(uploadRes)
    },
    resetCertFile() {
      this.application.certPhotoFile = null
      if (this.application.certPhotoObjectUrl) {
        URL.revokeObjectURL(this.application.certPhotoObjectUrl)
      }
      this.application.certPhotoObjectUrl = ''
      if (this.$refs.certFileInput) {
        this.$refs.certFileInput.value = ''
      }
    },
    formatCodeName(code, name) {
      if (code && name) return `${code}-${name}`
      return (code || name || '—')
    },
    formatEduLevel(val) {
      if (val === undefined || val === null || val === '') return '—'
      const k = String(val)
      return edulevel[k] != null ? edulevel[k] : '—'
    },
    triggerFileUpload() {
      this.$refs.certFileInput.click()
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      const allowedTypes = ['image/jpeg', 'image/png', 'image/bmp']
      if (!allowedTypes.includes(file.type)) {
        this.$message.warning('仅支持上传JPG、PNG、BMP格式的图片')
        this.$refs.certFileInput.value = ''
        return
      }
      if (this.application.certPhotoObjectUrl) {
        URL.revokeObjectURL(this.application.certPhotoObjectUrl)
      }
      this.application.certPhotoObjectUrl = URL.createObjectURL(file)
      this.application.certPhotoFile = file
      this.application.certPhotoName = file.name
    },
    getStatusClass(status) {
      if (status === 1) return 'status-pending'
      if (status === 2) return 'status-pass'
      if (status === 3 || status === 4) return 'status-reject'
      return ''
    },
    async submitApplication() {
      const certNo = this.application.certNo.trim()

      if (!certNo) {
        this.$message.warning('请输入证书编号')
        return
      }

      const isResubmit = this.application.status === 4

      if (!isResubmit && !this.selectedExam) {
        this.$message.warning('请选择考次')
        return
      }

      let certPhotoUrl = this.application.certPhotoUrl

      if (this.application.certPhotoFile) {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/bmp']
        if (!allowedTypes.includes(this.application.certPhotoFile.type)) {
          this.$message.warning('仅支持上传JPG、PNG、BMP格式的图片')
          return
        }
      } else if (!certPhotoUrl) {
        this.$message.warning('请上传证书图片')
        return
      }

      this.submitting = true
      try {
        if (this.application.certPhotoFile) {
          certPhotoUrl = await this.uploadCertPhoto(this.application.certPhotoFile)
          if (!certPhotoUrl) {
            this.$message.warning('证书图片上传失败，未获取到图片地址')
            return
          }
        }

        if (isResubmit) {
          await resubmitCertExempt({
            id: this.application.id,
            certNo,
            certPhotoUrl
          })
        } else {
          await submitCertExempt({
            certNo,
            batchId: this.selectedExam,
            certPhotoUrl
          })
        }

        this.$message.success('提交成功')
        // 保留本次上传图片的原始文件名，刷新后不再回显为服务端改名后的文件名
        const originalName = this.application.certPhotoFile ? this.application.certPhotoFile.name : ''
        await this.loadInitData()
        if (originalName) {
          this.application.certPhotoName = originalName
        }
        this.resetCertFile()
      } catch (e) {
        // 错误提示已由 request 封装统一处理
      } finally {
        this.submitting = false
      }
    },
    previewCertPhoto() {
      if (!this.certPhotoUrl) return
      const img = this.$refs.certPhotoImg.$el.querySelector('img')
      if (img) {
        img.click()
      } else {
        window.open(this.certPhotoUrl, '_blank')
      }
    }
  }
}
</script>

<style scoped>
.one-x-exemption {
  min-height: 500px;
}

.page-header {
  background-color: #fff;
  border-radius: 5px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.page-header-line {
  width: 4px;
  height: 22px;
  background-color: #0065BA;
  border-radius: 2px;
  margin-right: 10px;
  flex-shrink: 0;
}

.page-header h5 {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0;
  line-height: 60px;
}

.page-body {
  background-color: #fff;
  border-radius: 5px;
  padding: 20px;
}

.section {
  margin-bottom: 0;
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
}

.divider {
  height: 1px;
  background-color: #e8e8e8;
  margin: 16px 0;
}

/* 统一字段样式 */
.field-label {
  color: #767676;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

/* 必填标记 */
.required-mark {
  color: #f56c6c;
  margin-left: 2px;
}

.field-value {
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

/* 考次信息 */
.exam-info-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 40px;
}

.exam-info-item {
  display: flex;
  align-items: center;
}

.exam-select {
  width: 285px;
  max-width: 100%;
}

/* 未提交表单 */
.cert-form {
  padding: 0;
}

/* 该考次无此学生记录 */
.no-batch-tip {
  padding: 24px 0;
  color: #999;
  font-size: 14px;
  text-align: center;
}

.form-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  min-height: 36px;
}

.form-item .field-label {
  white-space: nowrap;
  line-height: 36px;
  width: 80px;
  flex-shrink: 0;
}

.form-item .field-value {
  line-height: 36px;
}

.form-input {
  width: 280px;
}

.form-item-inner {
  flex: 1;
}

.cert-upload {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.cert-file-input {
  display: none;
}

.upload-tip {
  font-size: 12px;
  color: #999;
}

.cert-file-name {
  margin-top: 6px;
  font-size: 13px;
  color: #666;
}

.submit-row {
  text-align: center;
  margin-top: 24px;
}

/* 已提交只读展示 */
.cert-display {
  padding: 0;
}

.cert-display-row {
  display: flex;
  flex-wrap: wrap;
  gap: 80px;
}

.cert-display-col {
  flex: 1;
  min-width: 200px;
}

.audit-feedback {
  margin-top: 16px;
  padding: 12px 14px;
  background: #F7F9FC;
  border-left: 3px solid #0168B7;
  border-radius: 5px;
}

.audit-feedback .certificate-field {
  margin-bottom: 0;
}

.audit-feedback-time {
  margin-top: 6px;
}

.certificate-field {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  min-height: 32px;
}

.certificate-field .field-label {
  white-space: nowrap;
  width: 80px;
  flex-shrink: 0;
}

.certificate-file-link {
  display: inline;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: #0168B7;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-decoration: underline;
  cursor: pointer;
}

.status-pending {
  color: #FF8A00;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.status-pass {
  color: #67C23A;
}

.status-reject {
  color: #F56C6C;
}

/* 日志时间线 */
.audit-timeline {
  position: relative;
}

.audit-timeline-item {
  position: relative;
  padding-left: 32px;
}

.audit-timeline-item + .audit-timeline-item {
  margin-top: 12px;
}

.audit-timeline-item:last-child {
  padding-bottom: 0;
}

.audit-timeline-line {
  position: absolute;
  left: 4px;
  top: 14px;
  width: 1px;
  height: 96px;
  background: #0168B7;
}

.timeline-dot {
  position: absolute;
  left: 0;
  top: 9px;
  width: 10px;
  height: 10px;
  border: 1px solid #0168B7;
  border-radius: 50%;
  background: #FFFFFF;
  box-sizing: border-box;
  z-index: 1;
}

.timeline-dot::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 4px;
  height: 4px;
  background: #0168B7;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.audit-log-card {
  width: 100%;
  min-height: 90px;
  padding: 18px 20px 14px;
  box-sizing: border-box;
  background: #F7F9FC;
  border: 0;
  border-radius: 5px;
  box-shadow: none;
}

.audit-log-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  align-items: center;
}

.audit-field {
  display: flex;
  align-items: center;
}

.audit-label {
  color: #767676;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.audit-value {
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.audit-status {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.audit-log-remark {
  margin-top: 10px;
  min-height: 20px;
  line-height: 20px;
}

@media (max-width: 1200px) {
  .audit-log-header {
    grid-template-columns: 2fr 1.2fr 1fr;
    column-gap: 16px;
  }
}

@media (max-width: 768px) {
  .audit-log-header {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
}
</style>